import React from 'react'
import { formatEuro, formatHours, formatInt, formatPercent } from '../../../shared/lib/formatNumberDe'
import {
  monthLongLabel,
  monthNumberLabel,
  monthShortLabel,
  toMonthKey,
  type DashboardMetric,
  type MonthlyPoint,
} from '../../../entities/dashboard'
import { niceScale, ratioColor } from '../model/chartScale'
import { TipRow, TipTitle, useChartTooltip } from '../model/useChartTooltip'
import { useElementWidth } from '../model/useElementWidth'

export const formatMetricValue = (metric: DashboardMetric, value: number): string =>
  metric === 'cost' ? formatEuro(value) : metric === 'hours' ? formatHours(value) : formatInt(value)

interface Props {
  series: MonthlyPoint[]
  metric: DashboardMetric
  now: Date
}

export const SollIstChart: React.FC<Props> = ({ series, metric, now }) => {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const { bind, tooltip } = useChartTooltip()

  const narrow = width < 520
  const height = narrow ? 250 : 300
  const left = narrow ? 44 : 58
  const right = 6
  const top = 26
  const bottom = narrow ? 40 : 54

  const maxValue = Math.max(1, ...series.flatMap((p) => [p.soll, p.ist]))
  const scale = niceScale(maxValue)
  const y = (v: number) => top + (height - top - bottom) * (1 - v / scale.max)
  const slot = (width - left - right) / Math.max(1, series.length)
  const barWidth = Math.max(5, Math.min(26, slot * 0.3))

  const ticks: number[] = []
  for (let v = 0; v <= scale.max + 1e-6; v += scale.step) ticks.push(v)

  const nowMonth = toMonthKey(now.getFullYear(), now.getMonth() + 1)
  const nowIndex = series.findIndex((p) => p.month === nowMonth)
  const daysInNowMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()

  return (
    <div ref={ref} className="w-full min-w-0">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block max-w-full"
        role="img"
        aria-label="Soll und Ist pro Monat"
      >
        {ticks.map((v) => (
          <g key={v}>
            <line x1={left} x2={width - right} y1={y(v)} y2={y(v)} stroke={v === 0 ? '#cbd5e1' : '#e5e7eb'} strokeWidth={1} />
            <text x={left - 8} y={y(v) + 4} textAnchor="end" fontSize={12} fill="#6b7280" fontFamily="Roboto, sans-serif">
              {formatInt(v)}
            </text>
          </g>
        ))}

        {series.map((point, i) => {
          const cx = left + slot * (i + 0.5)
          const phase = point.month < nowMonth ? 'past' : point.month === nowMonth ? 'now' : 'future'
          const ratio = point.soll > 0 ? (point.ist / point.soll) * 100 : null
          let ratioLabel = '–'
          let ratioFill = '#9ca3af'
          if (phase === 'now') {
            ratioLabel = 'läuft'
            ratioFill = '#6b7280'
          } else if (phase === 'past' && ratio != null) {
            ratioLabel = formatPercent(ratio)
            ratioFill = ratioColor(ratio)
          }
          return (
            <g
              key={point.month}
              {...bind(
                <>
                  <TipTitle>{monthLongLabel(point.month)}</TipTitle>
                  <TipRow label="Soll" value={formatMetricValue(metric, point.soll)} />
                  <TipRow label="Ist" value={formatMetricValue(metric, point.ist)} />
                  {ratio != null && phase !== 'future' && <TipRow label="Ist von Soll" value={formatPercent(ratio)} />}
                  <TipRow label="Aufträge" value={formatInt(point.orderCount)} />
                </>,
              )}
            >
              <rect x={cx - slot / 2} y={top} width={slot} height={height - top - bottom} fill="transparent" />
              {point.soll > 0 && (
                <rect
                  x={cx - barWidth - 1}
                  y={y(point.soll)}
                  width={barWidth}
                  height={Math.max(0, y(0) - y(point.soll))}
                  rx={3}
                  fill="#E6F0FB"
                  stroke="#124093"
                  strokeWidth={1.2}
                />
              )}
              {point.ist > 0 && (
                <rect
                  x={cx + 1}
                  y={y(point.ist)}
                  width={barWidth}
                  height={Math.max(0, y(0) - y(point.ist))}
                  rx={3}
                  fill="#004494"
                />
              )}
              <text x={cx} y={height - bottom + 17} textAnchor="middle" fontSize={12} fill="#374151" fontFamily="Roboto, sans-serif">
                {slot < 44 ? monthNumberLabel(point.month) : monthShortLabel(point.month)}
              </text>
              {!narrow && (
                <text
                  x={cx}
                  y={height - bottom + 36}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={500}
                  fill={ratioFill}
                  fontFamily="Roboto, sans-serif"
                >
                  {ratioLabel}
                </text>
              )}
            </g>
          )
        })}

        {!narrow && (
          <text x={left - 8} y={height - bottom + 36} textAnchor="end" fontSize={11} fill="#6b7280" fontFamily="Roboto, sans-serif">
            Ist/Soll
          </text>
        )}

        {nowIndex >= 0 && (
          <g>
            <line
              x1={left + slot * (nowIndex + now.getDate() / daysInNowMonth)}
              x2={left + slot * (nowIndex + now.getDate() / daysInNowMonth)}
              y1={top - 6}
              y2={y(0)}
              stroke="#6b7a94"
              strokeWidth={1.2}
              strokeDasharray="4 3"
            />
            <text
              x={Math.min(left + slot * (nowIndex + now.getDate() / daysInNowMonth), width - 24)}
              y={top - 11}
              textAnchor="middle"
              fontSize={11}
              fill="#6b7a94"
              fontFamily="Roboto, sans-serif"
            >
              heute
            </text>
          </g>
        )}
      </svg>
      {tooltip}
    </div>
  )
}
